import React from 'react'
import { StarIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'

interface StarRatingProps {
  rating: number | null | undefined
  onChange?: (rating: number) => void
  readonly?: boolean
  size?: 'sm' | 'md' | 'lg'
  max?: number
}

const StarRating: React.FC<StarRatingProps> = ({
  rating = 0,
  onChange,
  readonly = false,
  size = 'md',
  max = 5
}) => {
  const currentRating = rating || 0

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  }

  const handleClick = (starValue: number) => {
    if (!readonly && onChange) {
      // Si on clique sur l'étoile actuelle, on met à 0, sinon on met à la valeur cliquée
      const newRating = currentRating === starValue ? 0 : starValue
      onChange(newRating)
    }
  }

  if (readonly) return <span role="img" aria-label={`Favoris : ${currentRating} sur ${max}`} className="inline-flex items-center gap-1">
    {Array.from({ length: max }, (_, index) => {
      const Icon = index < currentRating ? StarSolidIcon : StarIcon
      return <Icon key={index} aria-hidden="true" className={`${sizeClasses[size]} ${index < currentRating ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`} />
    })}
  </span>

  return (
    <div role="group" aria-label="Favoris" className="flex items-center gap-1">
      {Array.from({ length: max }).map((_, index) => {
        const starValue = index + 1
        const isFilled = starValue <= currentRating
        const Icon = isFilled ? StarSolidIcon : StarIcon

        return (
          <button
            key={index}
            type="button"
            onClick={() => handleClick(starValue)}
            disabled={readonly}
            aria-pressed={currentRating === starValue}
            aria-label={`${starValue} étoile${starValue > 1 ? 's' : ''}`}
            className={`flex min-h-7 min-w-7 items-center justify-center rounded focus-visible:ring-2 focus-visible:ring-orange-600 transition-all ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} ${
              isFilled ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'
            }`}
            title={`${starValue} étoile${starValue > 1 ? 's' : ''}`}
          >
            <Icon aria-hidden="true" className={sizeClasses[size]} />
          </button>
        )
      })}
    </div>
  )
}

export default StarRating
